import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Check, ChevronDown, ChevronUp, Download, Inbox, Plus, Search } from '../../../icons';
import { listWorksites, updateWorksite, type WorksiteDto } from '../../../api/hr-worksite';
import {
  API_NEEDED, COMPANY_INFO_PATH, FOLLOWUP, LATER, WORKSITE_NEW_ID,
  addressLine, countFilters, errorText, filterWorksites, hasAddress, initialsOf, inputOf, teamsText, worksitePath,
  type WsFilter,
} from './worksite-v3';
import '../../HrMenu/employees/employees-hr.css';
import './worksites-v3.css';

/**
 * 통합설정 › 조직 › **사업장** 목록 — v3 (2026-09-14 · 사용자 결정 · 시안 `axi-settings-worksites`).
 *
 * 시안 그대로: KPI 넷 → 본사 안내 한 줄(회사정보로) → 사업장 목록 **전폭** 표(좌측 조직도 없음) → 빈 상태.
 * 행을 누르면 **별도 라우트** 상세(`/settings/worksites/:id`)로 간다(목록→상세는 화면 이동 · 2026-09-13 직원관리 결정).
 *
 * ★ 종전 `WorksiteSection`(한 화면 편집 + 근무지 배치)은 지우지 않았다 — 통합설정 칸만 이 화면으로 바꿔 건다(additive).
 * ★ 본사 행은 없다 — 본사는 회사정보가 정본이다.
 */
export function WorksitesListScreen(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [sites, setSites] = useState<WorksiteDto[]>([]);
  const [unassigned, setUnassigned] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 상세에서 지우고 돌아오면 무엇이 됐는지 한 줄 남긴다
  const [done, setDone] = useState<string | null>(() => (location.state as { done?: string } | null)?.done ?? null);
  const [filter, setFilter] = useState<WsFilter>('all');
  const [q, setQ] = useState('');
  /** 표시 순서 바꾸기 — 켜져 있으면 id 순서(보기 · 검색은 잠시 끈다). */
  const [order, setOrder] = useState<string[] | null>(null);

  /** `keepError` — 실패 뒤 목록을 다시 읽을 때 방금 띄운 안내를 지우지 않는다. */
  const load = useCallback(async (keepError = false) => {
    setLoading(true);
    if (!keepError) setError(null);
    try {
      const res = await listWorksites();
      setSites(res.worksites);
      setUnassigned(res.unassigned);
    } catch (err) {
      setError(errorText(err, '사업장을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const counts = useMemo(() => countFilters(sites), [sites]);
  const shown = useMemo(() => filterWorksites(sites, filter, q), [sites, filter, q]);
  const rows = order ? order.map((id) => sites.find((s) => s.id === id)).filter((s): s is WorksiteDto => !!s) : shown;
  const staffed = sites.reduce((n, s) => n + s.headcount, 0);
  const noAddress = sites.filter((s) => !hasAddress(s)).length;
  // 근무 기준 연결 — 서버 v3 는 칸을 주되 후속(마이그 200) 전에는 늘 null. 옛 서버는 칸이 없다.
  const scheduleKnown = sites.some((s) => s.defaultWorkSchedule !== undefined);
  const scheduleLinked = sites.filter((s) => s.defaultWorkSchedule).length;

  const move = (i: number, d: -1 | 1) => {
    if (!order) return;
    const j = i + d;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setOrder(next);
  };

  const saveOrder = async () => {
    if (!order) return;
    setBusy(true);
    setError(null);
    setDone(null);
    // 서버 순서표 일괄 저장이 없다 — 바뀐 행만 PATCH(목록에서 받은 값 그대로 + 표시 순서). v3 칸은 빼면 유지된다.
    // ★ 한 줄씩 보내므로 중간에 실패하면 앞 줄만 반영된다 — 몇 곳까지 됐는지 말한다(#1322 리뷰 비차단 ①).
    let changed = 0;
    try {
      for (const [i, id] of order.entries()) {
        const s = sites.find((x) => x.id === id);
        if (!s || s.sortOrder === i) continue;
        await updateWorksite(s.id, inputOf(s, { sortOrder: i }));
        changed += 1;
      }
      setOrder(null);
      setDone(changed > 0 ? `표시 순서를 저장했습니다 — ${changed}곳이 바뀌었습니다.` : '표시 순서가 그대로입니다.');
      await load();
    } catch (err) {
      const why = errorText(err, '표시 순서를 저장하지 못했습니다.');
      setError(changed > 0 ? `${why} — 앞의 ${changed}곳까지만 반영됐습니다. 목록을 다시 확인하세요.` : why);
      setOrder(null);
      await load(true);
    } finally {
      setBusy(false);
    }
  };

  const chip = (value: WsFilter, label: string) => (
    <label className="hre-choice">
      <input type="radio" name="wsv-filter" checked={filter === value} disabled={!!order} onChange={() => setFilter(value)} />
      <span>{label} {counts[value]}</span>
    </label>
  );

  return (
    <div className="hre wsv">
      <header className="hre-top">
        <div>
          <nav className="hre-crumb" aria-label="위치"><span>통합설정</span><i>/</i><span>조직</span><i>/</i><span>사업장</span></nav>
          <h1 className="hre-title">사업장</h1>
        </div>
        <label className="hre-search">
          <span className="hre-sr">사업장 검색</span>
          <Search aria-hidden />
          <input type="search" placeholder="사업장 이름 · 코드 · 주소" value={q} disabled={!!order} onChange={(e) => setQ(e.target.value)} />
        </label>
        <Link className="hre-btn" to={worksitePath(WORKSITE_NEW_ID)}><Plus aria-hidden />사업장 추가</Link>
      </header>

      {error && <p className="hre-alert" role="alert"><AlertCircle aria-hidden />{error}</p>}
      {done && !error && <p className="hre-ok" role="status"><Check aria-hidden />{done}</p>}

      <section className="hre-kpis" aria-label="사업장 지표">
        <article className="hre-card wsv-kpi">
          <div className="hre-cb">
            <div><span className="wsv-lb">사업장</span><b className="num">{sites.length}<small>곳</small></b></div>
            <span className="hre-badge hre-badge--slate">사용 {counts.active} · 쓰지 않음 {counts.inactive}</span>
          </div>
        </article>
        <article className="hre-card wsv-kpi">
          <div className="hre-cb">
            <div><span className="wsv-lb">사업장 소속 인원</span><b className="num">{staffed}<small>명</small></b></div>
            <span className="hre-badge hre-badge--ok">나머지 {unassigned}명은 본사</span>
          </div>
        </article>
        <article className="hre-card wsv-kpi">
          <div className="hre-cb">
            <div><span className="wsv-lb">주소 미입력</span><b className="num">{noAddress}<small>곳</small></b></div>
            <span className={`hre-badge hre-badge--${noAddress > 0 ? 'bad' : 'slate'}`}>증명서 주소에 쓰임</span>
          </div>
        </article>
        <article className="hre-card wsv-kpi">
          <div className="hre-cb">
            <div><span className="wsv-lb">근무 기준 연결</span><b className="num">{scheduleLinked}<small>/ {sites.length}곳</small></b></div>
            <span className="hre-badge hre-badge--slate">{scheduleKnown ? FOLLOWUP : API_NEEDED}</span>
          </div>
        </article>
      </section>

      <div className="wsv-hqnote">
        <b>본사</b>
        <span>본사 이름 · 주소 · 전화는 <b>통합설정 › 회사정보</b>에서 관리합니다 — 사업장이 없는 직원은 본사로 셉니다</span>
        <Link className="hre-btn hre-btn--line hre-btn--sm" to={COMPANY_INFO_PATH}>회사정보로 이동 ›</Link>
      </div>

      <section className="hre-card hre-listcard wsv-list" aria-label="사업장 목록">
        <div className="hre-cb">
          <div className="hre-ch">
            <h2>사업장 목록</h2>
            <div className="hre-end">
              <span className="hre-chips" role="radiogroup" aria-label="보기">
                {chip('all', '전체')}
                {chip('active', '사용')}
                {chip('inactive', '쓰지 않음')}
                {chip('noAddress', '주소 없음')}
              </span>
              <button type="button" className="hre-ghost" aria-label="내려받기" title={`내려받기 — ${LATER}`} disabled>
                <Download aria-hidden />
              </button>
            </div>
          </div>
          <span className="hre-path">
            {order ? '표시 순서 바꾸기 — 위 · 아래 단추로 옮기고 「순서 저장」' : '전체 사업장 · 행을 누르면 사업장 상세로 갑니다'}
          </span>

          {loading ? (
            <p className="hre-path">불러오는 중…</p>
          ) : sites.length === 0 ? (
            <div className="hre-empty">
              <Inbox aria-hidden />
              <b>아직 사업장이 없습니다</b>
              <span>본사만 있는 회사는 그대로 두면 됩니다(본사는 회사정보) — 지사가 생기면 사업장을 추가하세요</span>
              <Link className="hre-btn hre-btn--sm" to={worksitePath(WORKSITE_NEW_ID)}><Plus aria-hidden />사업장 추가</Link>
            </div>
          ) : rows.length === 0 ? (
            <div className="hre-empty">
              <Inbox aria-hidden />
              <b>조건에 맞는 사업장이 없습니다</b>
              <span>보기를 「전체」로 바꾸거나 검색어를 지우세요</span>
            </div>
          ) : (
            <div className="hre-tw">
              <table className="hre-table">
                <colgroup>
                  <col style={{ width: '23%' }} /><col style={{ width: '31%' }} /><col style={{ width: '23%' }} />
                  <col style={{ width: '13%' }} /><col style={{ width: '10%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>사업장 · 코드</th>
                    <th>주소 · 전화</th>
                    <th>소속 인원 · 팀</th>
                    <th>근무 기준<span className="wsv-ph wsv-ph--wait">{scheduleKnown ? FOLLOWUP : API_NEEDED}</span></th>
                    <th className="r">상태</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s, i) => {
                    const addr = addressLine(s);
                    const teams = teamsText(s.teams);
                    return (
                      <tr key={s.id} className={order ? undefined : 'hre-row'} onClick={order ? undefined : () => navigate(worksitePath(s.id))}>
                        <td>
                          <span className="hre-pickrow">
                            {order && (
                              <span className="wsv-order">
                                <button type="button" aria-label={`${s.name} 위로`} disabled={i === 0 || busy} onClick={() => move(i, -1)}><ChevronUp aria-hidden /></button>
                                <button type="button" aria-label={`${s.name} 아래로`} disabled={i === rows.length - 1 || busy} onClick={() => move(i, 1)}><ChevronDown aria-hidden /></button>
                              </span>
                            )}
                            <span className={`wsv-ico${s.isActive ? '' : ' wsv-ico--off'}`} aria-hidden>{initialsOf(s)}</span>
                            <span>
                              {order ? <b>{s.name}</b> : (
                                <Link to={worksitePath(s.id)} onClick={(e) => e.stopPropagation()}><b>{s.name}</b></Link>
                              )}
                              <small className="num">{s.code ?? '코드 없음'}</small>
                            </span>
                          </span>
                        </td>
                        <td>
                          {addr ? <span className="wsv-addr" title={addr}>{addr}</span> : <span className="hre-badge hre-badge--bad">주소 없음</span>}
                          <small className="wsv-sub num">{s.phone ?? '—'}</small>
                        </td>
                        <td>
                          <b className="num wsv-count">{s.headcount}명</b>
                          {teams !== null ? <small className="wsv-sub" title={teams}>{teams}</small> : (
                            <small className="wsv-sub">팀 집계<span className="wsv-ph">{API_NEEDED}</span></small>
                          )}
                        </td>
                        <td><span className="wsv-muted">{s.defaultWorkSchedule?.name ?? '회사 기본'}</span></td>
                        <td className="r">
                          <span className={`hre-badge hre-badge--${s.isActive ? 'ok' : 'slate'}`}>{s.isActive ? '사용' : '쓰지 않음'}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="hre-listfoot">
            <span className="num">사업장 {sites.length}곳 · 소속 {staffed}명</span>
            <span className="wsv-listfoot-acts">
              {order ? (
                <>
                  <button type="button" className="hre-btn hre-btn--line hre-btn--sm" disabled={busy} onClick={() => setOrder(null)}>취소</button>
                  <button type="button" className="hre-btn hre-btn--sm" disabled={busy} onClick={() => { void saveOrder(); }}>{busy ? '저장 중…' : '순서 저장'}</button>
                </>
              ) : (
                <button type="button" className="hre-btn hre-btn--line hre-btn--sm" disabled={sites.length < 2 || loading}
                  onClick={() => { setDone(null); setOrder(sites.map((s) => s.id)); }}>표시 순서 바꾸기</button>
              )}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}

export default WorksitesListScreen;
